/**
 * SECURITY: Input Sanitization Utilities
 *
 * Prevents XSS, SQL injection, command injection, and other injection attacks
 * by blocking dangerous characters in user input.
 *
 * Characters blocked:
 * - HTML/XML: < > / (prevents HTML/script injection)
 * - Quotes: ' " ` (can break out of HTML attributes and JS strings)
 * - Special chars: ; \ | & $ ^ * + = -- # % (used in various injection attacks)
 * - Brackets: ( ) { } [ ] (can be used in code injection)
 * - Note: Single - (hyphen) is ALLOWED, only -- (double dash, SQL comment) is blocked
 *
 * Email fields: Allow @ and - but block all other dangerous characters
 * Regular fields: Block all dangerous characters including @ (but allow single -)
 */

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const MAX_NAME_LENGTH = 50
export const MAX_EMAIL_LENGTH = 320
export const MAX_MESSAGE_LENGTH = 500

/** @param allowSpaces When true, does not trim; use while typing multi-word names/messages so spaces are not stripped on every keystroke. Still strips dangerous chars. Trim at submit. */
export function sanitizeInput(input: string, allowSpaces = false): string {
  if (!input || typeof input !== 'string') {
    return ''
  }

  const dangerousCharsRegex = /[<>'"`;\\/|&$^*+=(){}[\]#%]/g
  let sanitized = input.replace(dangerousCharsRegex, '')
  sanitized = sanitized.replace(/--/g, '')
  sanitized = sanitized.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    ''
  )
  sanitized = sanitized.replace(/<[^>]*>/g, '')
  sanitized = sanitized.replace(/(javascript|data|vbscript|on\w+)\s*:/gi, '')
  sanitized = sanitized.replace(/on\w+\s*=/gi, '')
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '')
  sanitized = sanitized.replace(/\0/g, '')

  return allowSpaces ? sanitized : sanitized.trim()
}

/**
 * Promo coupon code: same defenses as {@link sanitizeInput} but allows `%` and strips \\n/\\r/\\t.
 * Trim + NFC then dangerous-char stripping (keep server `sanitizeInput.js` in sync; parity tests).
 */
export function sanitizePromoCouponCode(input: string): string {
  if (!input || typeof input !== 'string') {
    return ''
  }

  let sanitized = input.trim()
  sanitized =
    typeof sanitized.normalize === 'function' ? sanitized.normalize('NFC') : sanitized

  const dangerousCharsRegex = /[<>'"`;\\/|&$^*+=(){}[\]#]/g
  sanitized = sanitized.replace(dangerousCharsRegex, '')
  sanitized = sanitized.replace(/--/g, '')
  sanitized = sanitized.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    ''
  )
  sanitized = sanitized.replace(/<[^>]*>/g, '')
  sanitized = sanitized.replace(/(javascript|data|vbscript|on\w+)\s*:/gi, '')
  sanitized = sanitized.replace(/on\w+\s*=/gi, '')
  sanitized = sanitized.replace(/[\n\r\t]/g, '')
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '')
  sanitized = sanitized.replace(/\0/g, '')

  return sanitized.trim()
}

/**
 * URL fields: strips control characters, newlines, and HTML-injection chars (< > " ' `),
 * but preserves all characters that are valid or percent-encoded in URLs
 * (/ : ? = & # % . - _ ~ ! * ( ) @ , ; + $).
 */
export function sanitizeWebsiteUrlInput(input: string): string {
  if (!input || typeof input !== 'string') {
    return ''
  }
  let sanitized = input
  // Strip HTML-dangerous chars that can never appear in a URL
  sanitized = sanitized.replace(/[<>"'`]/g, '')
  // Strip newlines / carriage returns
  sanitized = sanitized.replace(/[\n\r]/g, '')
  // Strip null bytes and other ASCII control chars
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '')
  sanitized = sanitized.replace(/\0/g, '')
  // Block javascript:/data:/vbscript: schemes
  sanitized = sanitized.replace(/(javascript|data|vbscript)\s*:/gi, '')
  return sanitized.trim()
}

export function sanitizeEmailInput(input: string): string {
  if (!input || typeof input !== 'string') {
    return ''
  }

  let sanitized = input.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    '',
  )
  sanitized = sanitized.replace(/<[^>]*>/g, '')
  const dangerousCharsRegex = /[<>'"`;\\/|&$^*+=(){}[\]#%]/g
  sanitized = sanitized.replace(dangerousCharsRegex, '')
  sanitized = sanitized.replace(/--/g, '')
  sanitized = sanitized.replace(/[\n\r]/g, '')
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '')
  sanitized = sanitized.replace(/\0/g, '')
  sanitized = sanitized.replace(/(javascript|data|vbscript|on\w+)\s*:/gi, '')
  sanitized = sanitized.replace(/on\w+\s*=/gi, '')

  return sanitized.trim()
}

export function containsDangerousChars(input: string): boolean {
  if (!input || typeof input !== 'string') {
    return false
  }

  const dangerousCharsRegex = /[<>'"`;\\/|&$^*+=(){}[\]#%]/
  if (dangerousCharsRegex.test(input)) {
    return true
  }
  if (/--/.test(input)) {
    return true
  }
  if (/<script|javascript:|data:|vbscript:|on\w+\s*=/i.test(input)) {
    return true
  }
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/.test(input)) {
    return true
  }

  return false
}

export function emailContainsDangerousChars(input: string): boolean {
  if (!input || typeof input !== 'string') {
    return false
  }

  const dangerousCharsRegex = /[<>'"`;\\/|&$^*+=(){}[\]#%]/
  if (dangerousCharsRegex.test(input)) {
    return true
  }
  if (/--/.test(input)) {
    return true
  }
  if (/[\n\r]/.test(input)) {
    return true
  }
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/.test(input)) {
    return true
  }

  return false
}

export function escapeHtml(text: string): string {
  if (!text) return ''
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  }
  return String(text).replace(/[&<>"']/g, (m) => map[m as keyof typeof map])
}
